import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DataGridPro, gridClasses } from "@mui/x-data-grid-pro";
import { v4 as uuidv4 } from "uuid";
import { LicenseInfo } from "@mui/x-license";
import useValueList from "../../hooks/useValueList";
import useUserFilters from "../../hooks/useUserFilters";
import { useBookmakers } from "../../contexts/BookmakersContext";
import CustomToolbar from "./CustomToolbar";
import FilterModelManager from "./FilterModelManager";
import FilterModelTutorial from "./FilterModelManagerTutorial";
import ValuePageHeader from "./ValuePageHeader";
import ValuePageFilters from "./ValuePageFilters";
import ValuePageColumns from "./ValuePageColumns";
import moment from "moment";
import KellyModal from "./KellyModal";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import GroupedBetModal from "./GroupedBetModal";
import BookieWeightsModal from "./BookieWeightsModal";
import HideBetModal from "./HideBetModal";
import HiddenBetsModal from "./HiddenBetsModal";
import useGroupedBet from "../../hooks/useGroupedBet";
import { EnhancedErrorHandler } from "../EnhancedErrorHandler";
import CustomHeaderFilter from "./CustomHeaderFilter";

LicenseInfo.setLicenseKey(
  "220fb8c36ee3c17d5d0a13a530887adcTz05NDA4MixFPTE3NTIyNjg2MDAwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI="
);

const defaultColumnVisibilityModelEvMode = {
  league: false,
  aveOdds: false,
  oddsAveRatio: false,
  firstSecondRatio: false,
  oddsFairRatio: false,
  fullKelly: false,
  halfKelly: false,
};

const defaultColumnVisibilityModelNonEvMode = {
  league: false,
  aveOdds: true,
  oddsAveRatio: true,
  firstSecondRatio: true,
  oddsFairRatio: false,
  fullKelly: false,
  halfKelly: false,
};

// Define default column width percentages for both modes
const evModeColumnWidthPercentages = {
  eventStart: 14,
  key: 12,
  market: 10,
  event: 12,
  bookie: 10,
  bookieCount: 6,
  rate: 6,
  bestAltOdds: 6,
  pinnacleFairOdds: 6,
  fairOdds: 6,
  ev: 6,
  quarterKelly: 6,
};

const nonEvModeColumnWidthPercentages = {
  eventStart: 20,
  key: 12,
  market: 13,
  event: 14,
  bookie: 10,
  bookieCount: 6,
  pinnacleFairOdds: 6,
  rate: 6,
  bestAltOdds: 6,
};

const ValuePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [evMode, setEvMode] = useState(() => {
    const storedEvMode = localStorage.getItem("valuePageEvMode");
    return storedEvMode ? JSON.parse(storedEvMode) : true;
  });
  const [columnWidths, setColumnWidths] = useState(() => {
    // Try to get stored widths first based on current mode
    const storedWidths = localStorage.getItem(
      evMode ? "valuePageColumnWidthsEv" : "valuePageColumnWidthsNonEv"
    );
    if (storedWidths) {
      try {
        return JSON.parse(storedWidths);
      } catch (e) {
        console.error("Failed to parse stored column widths");
      }
    }

    // Calculate default widths based on viewport if no stored widths
    const containerWidth = window.innerWidth - 48; // Account for padding
    const defaultWidths = {};
    const widthPercentages = evMode
      ? evModeColumnWidthPercentages
      : nonEvModeColumnWidthPercentages;
    Object.entries(widthPercentages).forEach(([field, percentage]) => {
      defaultWidths[field] = Math.floor((containerWidth * percentage) / 100);
    });
    return defaultWidths;
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    // Try to get stored order based on current mode
    const storedOrder = localStorage.getItem(
      evMode ? "valuePageColumnOrderEv" : "valuePageColumnOrderNonEv"
    );
    if (storedOrder) {
      try {
        return JSON.parse(storedOrder);
      } catch (e) {
        console.error("Failed to parse stored column order");
      }
    }
    return {};
  });

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    // Try to get stored visibility based on current mode
    const storedVisibility = localStorage.getItem(
      evMode ? "valuePageColumnVisibilityEv" : "valuePageColumnVisibilityNonEv"
    );
    if (storedVisibility) {
      try {
        return JSON.parse(storedVisibility);
      } catch (e) {
        console.error("Failed to parse stored column visibility");
      }
    }
    return evMode
      ? defaultColumnVisibilityModelEvMode
      : defaultColumnVisibilityModelNonEvMode;
  });

  // Update stored settings when mode changes
  useEffect(() => {
    // Load settings for the current mode
    const widthsKey = evMode
      ? "valuePageColumnWidthsEv"
      : "valuePageColumnWidthsNonEv";
    const orderKey = evMode
      ? "valuePageColumnOrderEv"
      : "valuePageColumnOrderNonEv";
    const visibilityKey = evMode
      ? "valuePageColumnVisibilityEv"
      : "valuePageColumnVisibilityNonEv";

    const storedWidths = localStorage.getItem(widthsKey);
    const storedOrder = localStorage.getItem(orderKey);
    const storedVisibility = localStorage.getItem(visibilityKey);

    if (storedWidths) {
      try {
        setColumnWidths(JSON.parse(storedWidths));
      } catch (e) {
        console.error("Failed to parse stored column widths");
      }
    } else {
      // Calculate default widths for the current mode
      const containerWidth = window.innerWidth - 48;
      const defaultWidths = {};
      const widthPercentages = evMode
        ? evModeColumnWidthPercentages
        : nonEvModeColumnWidthPercentages;
      Object.entries(widthPercentages).forEach(([field, percentage]) => {
        defaultWidths[field] = Math.floor((containerWidth * percentage) / 100);
      });
      setColumnWidths(defaultWidths);
    }

    if (storedOrder) {
      try {
        setColumnOrder(JSON.parse(storedOrder));
      } catch (e) {
        console.error("Failed to parse stored column order");
      }
    } else {
      setColumnOrder({});
    }

    if (storedVisibility) {
      try {
        setColumnVisibilityModel(JSON.parse(storedVisibility));
      } catch (e) {
        console.error("Failed to parse stored column visibility");
      }
    } else {
      setColumnVisibilityModel(
        evMode
          ? defaultColumnVisibilityModelEvMode
          : defaultColumnVisibilityModelNonEvMode
      );
    }
  }, [evMode]);

  // Handle column width changes
  const handleColumnWidthChange = useCallback(
    (params) => {
      const newWidths = {
        ...columnWidths,
        [params.colDef.field]: params.width,
      };
      setColumnWidths(newWidths);
      localStorage.setItem(
        evMode ? "valuePageColumnWidthsEv" : "valuePageColumnWidthsNonEv",
        JSON.stringify(newWidths)
      );
    },
    [columnWidths, evMode]
  );

  // Handle column visibility changes
  const handleColumnVisibilityModelChange = useCallback(
    (newModel) => {
      setColumnVisibilityModel(newModel);
      localStorage.setItem(
        evMode
          ? "valuePageColumnVisibilityEv"
          : "valuePageColumnVisibilityNonEv",
        JSON.stringify(newModel)
      );
    },
    [evMode]
  );

  // Handle column order changes
  const handleColumnOrderChange = useCallback(
    (params) => {
      const newOrder = {};
      const targetIndex = params.targetIndex;
      const field = params.column.field;

      if (typeof field === "string") {
        const columnsArray = columns
          .filter((col) => col.field !== field)
          .map((col) => col.field);

        columnsArray.splice(targetIndex, 0, field);

        columnsArray.forEach((columnField, index) => {
          newOrder[columnField] = index;
        });

        setColumnOrder(newOrder);
        localStorage.setItem(
          evMode ? "valuePageColumnOrderEv" : "valuePageColumnOrderNonEv",
          JSON.stringify(newOrder)
        );
      }
    },
    [evMode]
  );

  const [extraFilters, setExtraFilters] = useState({
    maxOdds: "",
    bookieCount: "",
    hoursToEvent: "",
    selectedSport: [],
    selectedBookmakers: [],
  });
  const { bookmakers } = useBookmakers();
  const [isFilterModelManagerOpen, setIsFilterModelManagerOpen] =
    useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [isKellyModalOpen, setIsKellyModalOpen] = useState(false);
  const [isBookieWeightsModalOpen, setIsBookieWeightsModalOpen] =
    useState(false);
  const [kellySettings, setKellySettings] = useState(null);
  const [hiddenBetsCount, setHiddenBetsCount] = useState(0);
  const [isHideBetModalOpen, setIsHideBetModalOpen] = useState(false);
  const [selectedBetToHide, setSelectedBetToHide] = useState(null);
  const [isHiddenBetsModalOpen, setIsHiddenBetsModalOpen] = useState(false);
  const [hiddenItems, setHiddenItems] = useState(() => {
    const stored = localStorage.getItem("hiddenItems");
    if (stored) {
      try {
        const items = JSON.parse(stored);
        // Auto-cleanup items older than 24 hours
        const now = moment();
        const filtered = items.filter((item) => {
          const eventTime = moment(item.eventStart);
          return now.diff(eventTime, "hours") < 24;
        });
        // If we filtered anything, update localStorage
        if (filtered.length !== items.length) {
          localStorage.setItem("hiddenItems", JSON.stringify(filtered));
        }
        return filtered;
      } catch (e) {
        console.error("Failed to parse hidden items:", e);
        return [];
      }
    }
    return [];
  });

  const [selectedGroupedBetId, setSelectedGroupedBetId] = useState(null);
  const [selectedGroupedBet, setSelectedGroupedBet] = useState(null);
  const [excludedBookies, setExcludedBookies] = useState([]);
  const {
    groupedBetData,
    loading: groupedBetLoading,
    error: groupedBetError,
    fetchGroupedBet,
  } = useGroupedBet(evMode);

  // Use separate hooks for Kelly settings and bookie weights
  const {
    filters: kellyFilters,
    loading: kellyFiltersLoading,
    error: kellyFiltersError,
    fetchFilters: fetchKellyFilters,
    saveFilter: saveKellyFilter,
  } = useUserFilters();

  const {
    filters: bookieWeightFilters,
    loading: bookieWeightFiltersLoading,
    error: bookieWeightFiltersError,
    fetchFilters: fetchBookieWeights,
    saveFilter: saveBookieWeights,
  } = useUserFilters();

  const [bookieWeights, setBookieWeights] = useState(null);

  // Format bookie weights for API calls
  const getBookieWeightsParam = useCallback(() => {
    if (!bookieWeights || Object.keys(bookieWeights).length === 0) {
      return "";
    }
    return Object.entries(bookieWeights)
      .map(([bookie, weight]) => `${bookie}:${weight}`)
      .join(",");
  }, [bookieWeights]);

  // Update API params with bookie weights
  const apiParams = useMemo(() => {
    const params = {
      sport: extraFilters.selectedSport?.length
        ? extraFilters.selectedSport.join(",")
        : undefined,
      value_bets: 1000,
      max_odds: extraFilters.maxOdds,
      bookies: extraFilters.selectedBookmakers.join(","),
      bookie_count: extraFilters.bookieCount,
      hours_to_event: extraFilters.hoursToEvent,
      ...(evMode ? {} : { non_ev_mode: true }),
    };

    // Add bookie weights if available
    const bookieWeightsParam = getBookieWeightsParam();
    if (!!bookieWeightsParam) {
      params.bookie_weights = bookieWeightsParam;
    }

    return params;
  }, [
    extraFilters.selectedSport,
    extraFilters.maxOdds,
    extraFilters.selectedBookmakers,
    extraFilters.bookieCount,
    extraFilters.hoursToEvent,
    evMode,
    getBookieWeightsParam,
    bookieWeights,
  ]);

  const { valueBets, loading, backgroundLoading, error, triggerRefresh } =
    useValueList(apiParams, autoRefresh);

  const rows = useMemo(
    () =>
      valueBets?.map((valueBet) => {
        const { bet, player, team, line } = valueBet;
        let key = `${bet}`;
        if (player && player !== bet) key += ` - ${player}`;
        if (team && team !== bet) key += ` - ${team}`;
        if (line && line !== bet) key += ` ${line}`;

        return {
          ...valueBet,
          id: uuidv4(),
          key,
        };
      }),
    [valueBets]
  );

  const [sortModel, setSortModel] = useState([]);

  const handleSelectFilterModel = useCallback((model) => {
    // Convert selectedSport to array if it's a string or null
    const selectedSport = model.selectedSport
      ? Array.isArray(model.selectedSport)
        ? model.selectedSport
        : [model.selectedSport]
      : [];

    setFilterModel(model);
    if (model.sortModel) {
      setSortModel(model.sortModel);
    }
    setExtraFilters({
      maxOdds: model.maxOdds || "",
      bookieCount: model.bookieCount || "",
      hoursToEvent: model.hoursToEvent || "",
      selectedSport: selectedSport,
      selectedBookmakers: model.selectedBookmakers || [],
    });
    localStorage.setItem(
      "lastSelectedFilterId",
      model.id ? model.id.toString() : ""
    );
  }, []);

  const resetFilterModel = useCallback(() => {
    handleSelectFilterModel({
      items: [],
      sortModel: [],
      maxOdds: "",
      bookieCount: "",
      hoursToEvent: "",
      selectedSport: [],
      selectedBookmakers: [],
    });
  }, [handleSelectFilterModel]);

  const handleExtraFiltersChange = useCallback((newFilters) => {
    setExtraFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters, ...newFilters };
      // Convert selectedSport to array if it's a string or null
      if ("selectedSport" in newFilters) {
        updatedFilters.selectedSport = newFilters.selectedSport
          ? Array.isArray(newFilters.selectedSport)
            ? newFilters.selectedSport
            : [newFilters.selectedSport]
          : [];
      }
      // Convert empty strings to null for API requests
      for (const key in updatedFilters) {
        if (updatedFilters[key] === "") {
          updatedFilters[key] = null;
        }
      }
      return updatedFilters;
    });
  }, []);

  const handleOpenFilterModelManager = useCallback(() => {
    setIsFilterModelManagerOpen(true);
  }, []);

  const handleCloseFilterModelManager = useCallback(() => {
    setIsFilterModelManagerOpen(false);
  }, []);

  // useEffect(() => {
  //   const hasSeenTutorial = localStorage.getItem("hasSeenFilterModelTutorial");
  //   if (!hasSeenTutorial) {
  //     setIsTutorialOpen(true);
  //   }
  // }, []);

  const effectiveColumnVisibilityModel = useMemo(() => {
    if (isMobile) {
      return {
        eventStart: false,
        key: false,
        market: false,
        league: false,
        event: false,
        bookie: false,
        bookieCount: false,
        rate: false,
        bestAltOdds: false,
        pinnacleFairOdds: false,
        aveOdds: false,
        fairOdds: false,
        oddsAveRatio: false,
        firstSecondRatio: false,
        oddsFairRatio: false,
        ev: false,
        quarterKelly: false,
        halfKelly: false,
        fullKelly: false,
        mobileCard: true,
      };
    }
    return columnVisibilityModel;
  }, [columnVisibilityModel, isMobile]);

  const getRowHeight = useCallback(() => {
    return isMobile ? "auto" : null;
  }, [isMobile]);

  const copyToBetTracker = useCallback((params) => {
    const {
      eventStart,
      bookie,
      event,
      key: selection,
      rate,
      market,
    } = params?.row;
    const formattedDate = moment(eventStart).format("YYYY-MM-DD");
    const formattedBookie = bookie === "Neds Group" ? "Neds" : bookie;
    const betTrackerData = `${formattedDate}\t${formattedBookie}\tMug\t\t${event}\t${market} ${selection}\t\t${rate}`;
    navigator.clipboard.writeText(betTrackerData);
  }, []);

  const handleOpenKellyModal = () => {
    setIsKellyModalOpen(true);
  };

  const handleCloseKellyModal = () => {
    setIsKellyModalOpen(false);
  };

  const handleSaveKellySettings = (newSettings) => {
    setKellySettings(newSettings);
    // Use the specific saveKellyFilter function
    saveKellyFilter({
      key: "kellySettings",
      content: newSettings,
      category: "value-betting",
    });
  };

  const handleOpenBookieWeightsModal = () => {
    setIsBookieWeightsModalOpen(true);
  };

  const handleCloseBookieWeightsModal = () => {
    setIsBookieWeightsModalOpen(false);
  };

  // Add a handler for when bookie weights are saved
  const handleSaveBookieWeights = (weights) => {
    setBookieWeights(weights);
    // Use the specific saveBookieWeights function
    saveBookieWeights({
      key: "bookieWeightConfig",
      content: weights,
      category: "value-betting",
    });

    // If there's a grouped bet selected, refresh it with the new weights
    if (selectedGroupedBetId) {
      const weightsParam = Object.entries(weights)
        .map(([bookie, weight]) => `${bookie}:${weight}`)
        .join(",");
      fetchGroupedBet(
        selectedGroupedBetId,
        evMode,
        excludedBookies,
        weightsParam
      );
    }
  };

  useEffect(() => {
    // Fetch kelly settings with its dedicated hook
    fetchKellyFilters("value-betting", "kellySettings");
    // Fetch bookie weights with its dedicated hook
    fetchBookieWeights("value-betting", "bookieWeightConfig");
  }, [fetchKellyFilters, fetchBookieWeights]);

  // Handle kelly settings update
  useEffect(() => {
    if (!!kellyFilters && !!kellyFilters.content) {
      setKellySettings(kellyFilters.content);
    }
  }, [kellyFilters]);

  // Handle bookie weights update
  useEffect(() => {
    if (!!bookieWeightFilters && !!bookieWeightFilters.content) {
      setBookieWeights(bookieWeightFilters.content);
    }
  }, [bookieWeightFilters]);

  // Update hidden bets count when hiddenItems changes
  useEffect(() => {
    setHiddenBetsCount(hiddenItems.length);
  }, [hiddenItems]);

  const openHideBetModal = useCallback((bet) => {
    setSelectedBetToHide(bet);
    setIsHideBetModalOpen(true);
  }, []);

  const handleHideItem = useCallback(
    (hideData) => {
      const updatedHiddenItems = [...hiddenItems, hideData];
      localStorage.setItem("hiddenItems", JSON.stringify(updatedHiddenItems));
      setHiddenItems(updatedHiddenItems);
    },
    [hiddenItems]
  );

  const handleUnhideItem = useCallback(
    (index, item) => {
      const updatedHiddenItems = hiddenItems.filter((_, i) => i !== index);
      localStorage.setItem("hiddenItems", JSON.stringify(updatedHiddenItems));
      setHiddenItems(updatedHiddenItems);
    },
    [hiddenItems]
  );

  const unhideAll = useCallback(() => {
    localStorage.removeItem("hiddenItems");
    setHiddenItems([]);
  }, []);

  const handleOpenHiddenBetsModal = useCallback(() => {
    setIsHiddenBetsModalOpen(true);
  }, []);

  const handleCloseHiddenBetsModal = useCallback(() => {
    setIsHiddenBetsModalOpen(false);
  }, []);

  const handleCloseHideBetModal = useCallback(() => {
    setIsHideBetModalOpen(false);
    setSelectedBetToHide(null);
  }, []);

  const filteredRows = useMemo(() => {
    return rows?.filter((bet) => {
      // Check each hidden item to see if this bet should be hidden
      return !hiddenItems.some((hiddenItem) => {
        // First check if event name and start time match
        if (
          hiddenItem.eventName !== bet.event ||
          hiddenItem.eventStart !== bet.eventStart
        ) {
          return false;
        }

        switch (hiddenItem.type) {
          case "entire_event":
            // Hide all bets for this event
            return true;

          case "market_event":
            // Hide all bets for this market in this event
            return hiddenItem.market === bet.market;

          case "market_team":
            // Hide bets for this market and team/player in this event
            return (
              hiddenItem.market === bet.market &&
              (hiddenItem.team === bet.team || hiddenItem.team === bet.player)
            );

          case "specific_bet_all_bookies":
            // Hide this specific bet from all bookies
            return (
              hiddenItem.bet === bet.bet &&
              hiddenItem.market === bet.market &&
              hiddenItem.player === bet.player &&
              hiddenItem.team === bet.team &&
              hiddenItem.line === bet.line
            );

          case "specific_bet":
            // Hide this specific bet
            return (
              hiddenItem.bet === bet.bet &&
              hiddenItem.bookie === bet.bookie &&
              hiddenItem.market === bet.market &&
              hiddenItem.player === bet.player &&
              hiddenItem.team === bet.team &&
              hiddenItem.line === bet.line
            );

          default:
            return false;
        }
      });
    });
  }, [rows, hiddenItems]);

  const handleGroupedBetClick = (row) => {
    setSelectedGroupedBetId(row.groupedBetId);
    setSelectedGroupedBet(row);
    setExcludedBookies([]);
    const hasFairOdds = row.fairOdds && row.fairOdds > 0;
    fetchGroupedBet(row.groupedBetId, hasFairOdds, [], getBookieWeightsParam());
  };

  const handleRefetchGroupedBet = (newExcludedBookies) => {
    setExcludedBookies(newExcludedBookies);
    if (groupedBetData && selectedGroupedBetId) {
      const hasFairOdds =
        selectedGroupedBet?.fairOdds && selectedGroupedBet?.fairOdds > 0;
      fetchGroupedBet(
        selectedGroupedBetId,
        hasFairOdds,
        newExcludedBookies,
        getBookieWeightsParam()
      );
    }
  };

  const { mobileColumns, desktopColumns } = ValuePageColumns({
    columnWidths,
    handleColumnWidthChange,
    columnVisibilityModel,
    copyToBetTracker,
    isMobile,
    kellySettings,
    hideBet: openHideBetModal,
    handleGroupedBetClick,
    evMode,
    columnOrder,
  });

  const columns = useMemo(() => {
    return isMobile ? mobileColumns : desktopColumns;
  }, [isMobile, mobileColumns, desktopColumns]);

  const customGridStyles = useMemo(
    () => ({
      [`& .${gridClasses.cell}`]: {
        padding: isMobile ? "0px !important" : undefined,
      },
    }),
    [isMobile]
  );

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setEvMode(newMode);
      localStorage.setItem("valuePageEvMode", JSON.stringify(newMode));
    }
  };

  return (
    <Box
      sx={{
        margin: { xs: "24px 8px", sm: 3 },
        position: "relative",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        {window.membershipLevel === 30 && (
          <>
            <ValuePageHeader
              autoRefresh={autoRefresh}
              onAutoRefreshToggle={() => setAutoRefresh(!autoRefresh)}
              onOpenKellyModal={handleOpenKellyModal}
              onOpenBookieWeightsModal={handleOpenBookieWeightsModal}
              onRefresh={triggerRefresh}
              backgroundLoading={backgroundLoading}
              onUnhideAll={unhideAll}
              hiddenBetsCount={hiddenBetsCount}
              onOpenHiddenBetsModal={handleOpenHiddenBetsModal}
              evMode={evMode}
              onModeChange={handleModeChange}
              hasCustomBookieWeights={
                bookieWeights && Object.keys(bookieWeights).length > 0
              }
            />

            <ValuePageFilters
              extraFilters={extraFilters}
              bookmakers={bookmakers}
              onExtraFiltersChange={handleExtraFiltersChange}
            />
          </>
        )}

        <EnhancedErrorHandler error={error} />
        {!error && window.membershipLevel !== 30 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "40px 24px",
              width: "100%",
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(145deg, ${theme.palette.secondary.dark}, rgba(0, 0, 0, 0.2))`
                  : `linear-gradient(145deg, ${theme.palette.secondary.light}, rgba(255, 255, 255, 0.9))`,
              borderRadius: "16px",
              flexDirection: "column",
              gap: 3,
              position: "relative",
              overflow: "hidden",
              boxShadow: "inset 0 0 20px rgba(0, 0, 0, 0.1)",
              border: `1px solid ${theme.palette.divider}`,
              my: 4,
            }}
          >
            <LockIcon
              sx={{
                fontSize: "48px",
                color: theme.palette.primary.main,
                opacity: 0.8,
                mb: 1,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                textAlign: "center",
                fontWeight: 600,
                mb: 1,
              }}
            >
              Pro Membership Required
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                textAlign: "center",
                maxWidth: "500px",
                mb: 2,
              }}
            >
              Value betting is available exclusively to Pro members. Upgrade
              your membership to access this feature.
            </Typography>
            <Button
              component="a"
              href="https://bonusbank.com.au/membership-plans/"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="primary"
              startIcon={<LockOpenIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "28px",
                padding: "12px 32px",
                fontSize: "1.1rem",
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                boxShadow: theme.shadows[4],
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              Upgrade to Pro
            </Button>
          </Box>
        ) : (
          <Box sx={{ width: "100%", height: "90vh" }}>
            <DataGridPro
              rows={filteredRows}
              columns={columns}
              columnVisibilityModel={effectiveColumnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
              getRowHeight={getRowHeight}
              loading={loading}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              getRowId={(row) => row.id}
              sortingOrder={["desc", "asc"]}
              slots={{
                toolbar: CustomToolbar,
                headerFilterCell: CustomHeaderFilter,
              }}
              slotProps={{
                toolbar: {
                  onOpenFilterModelManager: handleOpenFilterModelManager,
                  currentFilterModel: {
                    ...filterModel,
                    sortModel,
                  },
                  onDeselectFilterModel: resetFilterModel,
                },
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              headerFilters
              pagination
              filterModel={filterModel}
              onFilterModelChange={(newModel) => setFilterModel(newModel)}
              initialState={{
                columns: {
                  columnVisibilityModel: columnVisibilityModel,
                },
                pagination: {
                  paginationModel: { pageSize: 50 },
                },
              }}
              localeText={{
                headerFilterOperatorMarket: "Market",
              }}
              onColumnWidthChange={handleColumnWidthChange}
              onColumnOrderChange={handleColumnOrderChange}
              sx={{
                ...customGridStyles,
              }}
              sortModel={sortModel}
              onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            />
          </Box>
        )}

        {window.membershipLevel === 30 && (
          <>
            <FilterModelManager
              open={isFilterModelManagerOpen}
              onClose={handleCloseFilterModelManager}
              onSelectFilterModel={handleSelectFilterModel}
              filterModel={{ ...filterModel, ...extraFilters }}
            />
            <FilterModelTutorial
              isOpen={isTutorialOpen}
              setIsOpen={setIsTutorialOpen}
              isFilterModelManagerOpen={isFilterModelManagerOpen}
              openFilterModelManager={handleOpenFilterModelManager}
              closeFilterModelManager={handleCloseFilterModelManager}
            />
            <KellyModal
              open={isKellyModalOpen}
              kellySettings={kellySettings}
              onClose={handleCloseKellyModal}
              onSave={handleSaveKellySettings}
            />
            <BookieWeightsModal
              open={isBookieWeightsModalOpen}
              onClose={handleCloseBookieWeightsModal}
              onSave={handleSaveBookieWeights}
            />
            <HideBetModal
              open={isHideBetModalOpen}
              onClose={handleCloseHideBetModal}
              bet={selectedBetToHide}
              onHide={handleHideItem}
            />
            <HiddenBetsModal
              open={isHiddenBetsModalOpen}
              onClose={handleCloseHiddenBetsModal}
              hiddenItems={hiddenItems}
              onUnhide={handleUnhideItem}
              onUnhideAll={unhideAll}
            />
          </>
        )}
        <GroupedBetModal
          open={!!selectedGroupedBetId}
          onClose={() => setSelectedGroupedBetId(null)}
          groupedBetData={groupedBetData}
          loading={groupedBetLoading}
          error={groupedBetError}
          bet={selectedGroupedBet?.bet}
          fullBet={selectedGroupedBet?.fullBet}
          league={selectedGroupedBet?.league}
          market={selectedGroupedBet?.market}
          event={selectedGroupedBet?.event}
          event_id={selectedGroupedBet?.event_id}
          evMode={evMode}
          onRefetchGroupedBet={handleRefetchGroupedBet}
          excludedBookies={excludedBookies}
        />
      </Box>
    </Box>
  );
};

export default ValuePage;
